import { supabase } from '../../config/supabase';

/**
 * Check if a job with the given unique_id exists
 * @param {string} uniqueId - The unique ID to check
 * @param {string} companyId - The company ID
 * @returns {Promise<boolean>} True if job exists, false otherwise
 */
export const checkJobExists = async (uniqueId, companyId) => {
    try {
        const { data, error } = await supabase
            .from('jobs')
            .select('id')
            .eq('unique_id', uniqueId)
            .eq('company_id', companyId)
            .eq('deleted', false)
            .maybeSingle();

        if (error) throw error;
        return !!data;
    } catch (error) {
        console.error('Error checking job existence:', error);
        throw error;
    }
};

/**
 * Duplicate a job with all its related data
 * @param {string} sourceJobId - The ID of the job to duplicate
 * @param {string} newUniqueId - The new unique ID for the duplicated job
 * @param {string} companyId - The company ID
 * @param {string} userId - The user ID performing the duplication
 * @param {string} userName - The user name performing the duplication
 * @returns {Promise<Object>} The duplicated job data
 */
// export const duplicateJob = async (sourceJobId, newUniqueId, companyId, userId, userName) => {
//     try {
//         // 1. Check if the new unique_id already exists
//         const exists = await checkJobExists(newUniqueId, companyId);
//         if (exists) {
//             throw new Error('A job with this ID already exists');
//         }

//         // 2. Fetch the source job with all related data
//         const { data: sourceJob, error: fetchError } = await supabase
//             .from('jobs')
//             .select(`
//                 *,
//                 job_quotes(*),
//                 job_designers(*),
//                 job_estimators(*),
//                 job_production(*),
//                 job_deliveries(*),
//                 job_notes(*),
//                 job_files(*),
//                 job_floors(*),
//                 job_floor_specs(*),
//                 job_action_logs(*)
//             `)
//             .eq('id', sourceJobId)
//             .eq('company_id', companyId)
//             .eq('deleted', false)
//             .single();

//         if (fetchError) throw fetchError;
//         if (!sourceJob) throw new Error('Source job not found');

//         // 3. Create the new job (main record)
//         const {
//             id,
//             created_at,
//             updated_at,
//             short_id,
//             uid_prefix,
//             uid_number,
//             job_quotes,
//             job_designers,
//             job_estimators,
//             job_production,
//             job_deliveries,
//             job_notes,
//             job_files,
//             job_floors,
//             job_floor_specs,
//             job_action_logs,
//             ...jobDataToDuplicate
//         } = sourceJob;

//         const { data: newJob, error: jobError } = await supabase
//             .from('jobs')
//             .insert({
//                 ...jobDataToDuplicate,
//                 unique_id: newUniqueId,
//                 created_by_user_id: userId,
//                 created_by_name: userName,
//             })
//             .select()
//             .single();

//         if (jobError) throw jobError;

//         const quoteIdMap = new Map();

//         // 4. Duplicate job_quotes
//         if (job_quotes && job_quotes.length > 0) {
//             const quotesToInsert = job_quotes.map(q => {
//                 const { id, job_id, created_at, updated_at, ...data } = q; // ✅ exclude timestamps (optional but recommended)
//                 return {
//                     ...data,
//                     job_id: newJob.id
//                 };
//             });

//             const { data: newQuotes, error: quotesError } = await supabase
//                 .from('job_quotes')
//                 .insert(quotesToInsert)
//                 .select();

//             if (quotesError) throw quotesError;

//             // ✅ SAFE mapping using quote_type
//             newQuotes.forEach(newQuote => {
//                 const oldQuote = job_quotes.find(q => q.quote_type === newQuote.quote_type);
//                 if (oldQuote) {
//                     quoteIdMap.set(oldQuote.id, newQuote.id);
//                 }
//             });
//         }


//         // 5. Duplicate job_designers (exact copy, fix relations only)
//         if (job_designers && job_designers.length > 0) {
//             const designersToInsert = job_designers.map(designer => {
//                 const {
//                     id,
//                     job_id,
//                     created_at,
//                     updated_at,
//                     ...designerData
//                 } = designer;

//                 return {
//                     ...designerData,
//                     job_id: newJob.id, // ✅ new job relation

//                     // ✅ if child_job_id exists, point it to new job too
//                     child_job_id: designer.child_job_id ? newJob.id : null
//                 };
//             });

//             const { error: designersError } = await supabase
//                 .from('job_designers')
//                 .insert(designersToInsert);

//             if (designersError) throw designersError;
//         }


//         // 6. Duplicate job_estimators (exact copy, fix relation only)
//         if (job_estimators) {
//             const estimatorData = Array.isArray(job_estimators) ? job_estimators[0] : job_estimators;

//             if (estimatorData && Object.keys(estimatorData).length > 0) {
//                 const {
//                     id,
//                     job_id,
//                     created_at,
//                     updated_at,
//                     ...dataToInsert
//                 } = estimatorData;

//                 const { error: estimatorsError } = await supabase
//                     .from('job_estimators')
//                     .insert({
//                         ...dataToInsert,   // ✅ exact copy of all fields
//                         job_id: newJob.id, // ✅ new job relation
//                     });

//                 if (estimatorsError) throw estimatorsError;
//             }
//         }



//         // 7. Duplicate job_production (exact copy, fix job_id only)
//         if (job_production && job_production.length > 0) {
//             const productionToInsert = job_production.map(prod => {
//                 const {
//                     id,
//                     job_id,
//                     created_at,
//                     updated_at,
//                     ...productionData
//                 } = prod;

//                 return {
//                     ...productionData,   // ✅ exact copy of everything else
//                     job_id: newJob.id    // ✅ new job relation
//                 };
//             });

//             const { error: productionError } = await supabase
//                 .from('job_production')
//                 .insert(productionToInsert);

//             if (productionError) throw productionError;
//         }


//         // 8. Duplicate job_deliveries (UNIQUE constraint on job_id, quote_type, delivery_count)
//         if (job_deliveries && job_deliveries.length > 0) {
//             const deliveriesToInsert = job_deliveries.map(delivery => {
//                 const {
//                     id,
//                     job_id,
//                     created_at,
//                     updated_at,
//                     note_updated_at,
//                     assigned_at,
//                     scheduled_at,
//                     delivered_at,
//                     ...deliveryData
//                 } = delivery;

//                 return {
//                     ...deliveryData,
//                     job_id: newJob.id // ✅ ONLY this relation must change
//                 };
//             });


//             const { error: deliveriesError } = await supabase
//                 .from('job_deliveries')
//                 .insert(deliveriesToInsert);

//             if (deliveriesError) throw deliveriesError;
//         }

//         if (job_notes && job_notes.length > 0) {
//             const notesToInsert = job_notes.map(note => {
//                 const {
//                     id,
//                     job_id,
//                     created_at,
//                     updated_at,
//                     added_at,
//                     edited_at,
//                     ...noteData
//                 } = note;

//                 return {
//                     ...noteData,
//                     job_id: newJob.id // ✅ new relation
//                 };
//             });

//             const { error } = await supabase
//                 .from('job_notes')
//                 .insert(notesToInsert);

//             if (error) throw error;
//         }


//         // 10. Duplicate job_files (DB + Storage)
//         if (job_files && job_files.length > 0) {
//             const filesToInsert = [];

//             for (const file of job_files) {
//                 const {
//                     id,
//                     job_id,
//                     created_at,
//                     uploaded_at,
//                     ...fileData
//                 } = file;

//                 // 1) Extract old storage path from URL
//                 const oldUrl = file.url;
//                 const bucketName = 'job-files';

//                 // Example URL:
//                 // https://xxx.supabase.co/storage/v1/object/public/job-files/companySlug/jobId/file.pdf
//                 const pathMatch = oldUrl.split(`/storage/v1/object/public/${bucketName}/`)[1];

//                 if (!pathMatch) continue;

//                 // pathMatch = companySlug/jobId/file.pdf
//                 const parts = pathMatch.split('/');
//                 const companySlug = parts[0];
//                 const oldJobId = parts[1];
//                 const filename = parts.slice(2).join('/');

//                 const oldPath = `${companySlug}/${oldJobId}/${filename}`;
//                 const newPath = `${companySlug}/${newJob.id}/${filename}`;

//                 // 2) Copy file in Supabase Storage
//                 const { error: copyError } = await supabase.storage
//                     .from(bucketName)
//                     .copy(oldPath, newPath);

//                 if (copyError) {
//                     console.error('File copy failed:', copyError);
//                     throw copyError;
//                 }

//                 // 3) Build new public URL
//                 const { data: publicUrlData } = supabase.storage
//                     .from(bucketName)
//                     .getPublicUrl(newPath);

//                 const newUrl = publicUrlData.publicUrl;

//                 // 4) Prepare DB insert
//                 filesToInsert.push({
//                     ...fileData,
//                     job_id: newJob.id,
//                     url: newUrl
//                 });
//             }

//             // 5) Insert duplicated file records
//             const { error: filesError } = await supabase
//                 .from('job_files')
//                 .insert(filesToInsert);

//             if (filesError) throw filesError;
//         }


//         // 11. Duplicate job_floors + build ID map
//         let floorIdMap = new Map();

//         if (job_floors && job_floors.length > 0) {
//             const floorsToInsert = job_floors.map(floor => {
//                 const { id, job_id, created_at, updated_at, ...floorData } = floor;
//                 return {
//                     ...floorData,
//                     job_id: newJob.id,
//                 };
//             });

//             const { data: newFloors, error: floorsError } = await supabase
//                 .from('job_floors')
//                 .insert(floorsToInsert)
//                 .select();

//             if (floorsError) throw floorsError;

//             // ✅ SAFER mapping: old floor -> new floor
//             job_floors.forEach(oldFloor => {
//                 const matchedNewFloor = newFloors.find(f => f.floor_number === oldFloor.floor_number);
//                 if (matchedNewFloor) {
//                     floorIdMap.set(oldFloor.id, matchedNewFloor.id);
//                 }
//             });
//         }


//         // 12. Duplicate job_floor_specs
//         if (job_floor_specs && job_floor_specs.length > 0) {
//             const floorSpecsToInsert = job_floor_specs.map(spec => {
//                 const { id, job_id, created_at, updated_at, ...specData } = spec;

//                 return {
//                     ...specData,
//                     job_id: newJob.id // ✅ only this changes
//                 };
//             });

//             const { error: floorSpecsError } = await supabase
//                 .from('job_floor_specs')
//                 .insert(floorSpecsToInsert);

//             if (floorSpecsError) throw floorSpecsError;
//         }


//         if (job_action_logs && job_action_logs.length > 0) {
//             const actionLogsToInsert = job_action_logs.map(log => {
//                 const {
//                     id,
//                     job_id,
//                     timestamp, // remove old timestamp
//                     ...logData
//                 } = log;

//                 return {
//                     ...logData,
//                     job_id: newJob.id,
//                     timestamp: new Date() // ✅ optional: reset time
//                 };
//             });

//             const { error } = await supabase
//                 .from('job_action_logs')
//                 .insert(actionLogsToInsert);

//             if (error) throw error;
//         }


//         return newJob;
//     } catch (error) {
//         console.error('Error duplicating job:', error);
//         throw error;
//     }
// };

async function duplicateJobFiles(job_files, newJobId) {
    const bucket = 'job-files';

    await Promise.all(job_files.map(async file => {
        const oldUrl = file.url;
        const path = decodeURIComponent(
            oldUrl.split(`/storage/v1/object/public/${bucket}/`)[1]
        );

        const [companySlug, oldJobId, ...rest] = path.split('/');
        const filename = rest.join('/');

        const uniqueName = `${Date.now()}-${filename}`;
        const newPath = `${companySlug}/${newJobId}/${uniqueName}`;

        const { error: copyError } = await supabase.storage
            .from(bucket)
            .copy(path, newPath);

        if (copyError) throw copyError;

        const { data } = supabase.storage.from(bucket).getPublicUrl(newPath);

        await supabase.from('job_files').insert({
            ...file,
            id: undefined,
            job_id: newJobId,
            url: data.publicUrl,
            filename: uniqueName,
        });
    }));
}


export const duplicateJob = async (sourceJobId, newUniqueId, userId, userName) => {
    // 1) Call RPC
    const { data: newJobId, error } = await supabase.rpc('duplicate_job', {
        p_source_job_id: sourceJobId,
        p_new_unique_id: newUniqueId,
        p_user_id: userId,
        p_user_name: userName,
    });

    if (error) throw error;

    // 2) Fetch old job files
    const { data: files } = await supabase
        .from('job_files')
        .select('*')
        .eq('job_id', sourceJobId);

    // 3) Copy storage files
    await duplicateJobFiles(files, newJobId);

    return newJobId;
};

